import React, { useState, useEffect } from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import { useParams } from "react-router-dom";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import Loading from "../components/Loading";
import { useHistory } from "react-router-dom";
import { fetchOrganisation, fetchOrganisationMembers, addOrganisationMember, updateOrganisationMember, rotateOrgMemberKey, fetchOrgRoles, setOrgMemberRole, deleteOrganisation, fetchOrgSettings, saveOrgSettings, rotateSigningKey } from "../utils/apiService";

/** Loads one settings section for the org and saves changes back; errors are shown as text, never as raw API detail. */
const useOrgSettings = (section, orgId) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const token = async () => { try { return await getAccessTokenSilently(); } catch (e) { return null; } };
  useEffect(() => {
    let live = true;
    (async () => {
      try { const d = await fetchOrgSettings(section, await token(), orgId); if (live) { setData(d); setError(null); } }
      catch (e) { if (live) setError("These settings could not be loaded."); }
    })();
    return () => { live = false; };
    // token() only wraps getAccessTokenSilently
  }, [section, orgId, getAccessTokenSilently]);
  const save = async (value) => {
    try { const d = await saveOrgSettings(section, value, await token(), orgId); setData(prev => ({ ...(prev || {}), ...d })); setError(null); return null; }
    catch (e) { const message = e.userMessage || "Saving failed. Please try again."; setError(message); return message; }
  };
  const refresh = async () => {
    try { setData(await fetchOrgSettings(section, await token(), orgId)); } catch (e) { setError("These settings could not be loaded."); }
  };
  return { data, error, save, refresh, token };
};


const CURRENCY_MAP = {
  1: 'Not set', 840: 'USD — US Dollar', 978: 'EUR — Euro',
  826: 'GBP — British Pound', 36: 'AUD — Australian Dollar',
  124: 'CAD — Canadian Dollar', 756: 'CHF — Swiss Franc',
  392: 'JPY — Japanese Yen', 156: 'CNY — Chinese Yuan',
  356: 'INR — Indian Rupee', 985: 'PLN — Polish Złoty',
};

const BUSINESS_TYPE_MAP = { 1: 'Unknown', 2: 'Default' };

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'members', label: 'Members' },
  { id: 'sso', label: 'SSO' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'security', label: 'Security' },
];

const Field = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd' }}>
      {label}
    </div>
    <div style={{ fontSize: '0.9rem', color: '#1a1d23', fontWeight: 500 }}>
      {value || <span style={{ color: '#ced4da' }}>—</span>}
    </div>
  </div>
);

const SSO_PROVIDERS = [
  { name: 'Okta',       domain: 'okta.com' },
  { name: 'Azure AD',   domain: 'microsoft.com' },
  { name: 'Google',     domain: 'workspace.google.com' },
  { name: 'OneLogin',   domain: 'onelogin.com' },
  { name: 'Auth0',      domain: 'auth0.com' },
  { name: 'Custom',     domain: 'openid.net' },
];

const SsoTab = ({ orgId }) => {
  const sso = useOrgSettings('sso', orgId);
  const configs = (sso.data?.values || []).map(c => ({ ...c, providerDomain: SSO_PROVIDERS.find(p => p.name === c.provider)?.domain || 'openid.net' }));
  const storable = (list) => list.map(({ providerDomain, ...c }) => c);
  const [showPanel, setShowPanel] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', provider: '', domain: '', metadataUrl: '', protocol: 'SAML 2.0' });
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditingId(null);
    setForm({ name: '', provider: '', domain: '', metadataUrl: '', protocol: 'SAML 2.0' });
    setShowPanel(true);
  };

  const openEdit = (cfg) => {
    setEditingId(cfg.id);
    setForm({ name: cfg.name, provider: cfg.provider, domain: cfg.domain, metadataUrl: cfg.metadataUrl || '', protocol: cfg.protocol || 'SAML 2.0' });
    setShowPanel(true);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    const next = editingId
      ? configs.map(c => c.id === editingId ? { ...c, ...form } : c)
      : [...configs, { ...form, status: 'inactive' }];
    const err = await sso.save(storable(next));
    setSaving(false);
    if (err) window.alert(err);
    else setShowPanel(false);
  };

  const handleToggleStatus = (id) => {
    sso.save(storable(configs.map(c => c.id === id ? { ...c, status: c.status === 'active' ? 'inactive' : 'active' } : c)));
  };

  const handleDelete = (id) => {
    sso.save(storable(configs.filter(c => c.id !== id)));
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1d23' }}>Single Sign-On</div>
          <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '2px' }}>Manage SSO configurations available to members of this team</div>
        </div>
        <button
          onClick={openNew}
          style={{
            fontSize: '0.82rem', fontWeight: 600, padding: '8px 16px',
            borderRadius: '8px', border: 'none', background: 'var(--color-primary)', color: '#fff',
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          + Add SSO
        </button>
      </div>

      {/* Info banner */}
      <div style={{ background: '#f0f5ff', border: '1px solid #c8e6ff', borderRadius: '10px', padding: '0.9rem 1.1rem', marginBottom: '1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '1rem', flexShrink: 0 }}>ℹ️</span>
        <div style={{ fontSize: '0.75rem', color: '#3d6aad', lineHeight: 1.6 }}>
          SSO configurations added here will be available to members when connecting suppliers. Your IT admin must configure the corresponding SAML or OIDC application on the identity provider side.
        </div>
      </div>

      {/* Config list */}
      {configs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', background: '#f8f9fa', borderRadius: '12px', border: '1px dashed #dee2e6' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🔐</div>
          <div style={{ fontWeight: 600, color: '#343a40', marginBottom: '0.4rem' }}>No SSO configurations yet</div>
          <div style={{ fontSize: '0.78rem', color: '#adb5bd', marginBottom: '1rem' }}>Add an identity provider to let members sign in with SSO</div>
          <button onClick={openNew} style={{ fontSize: '0.82rem', fontWeight: 600, padding: '8px 18px', borderRadius: '8px', border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer' }}>
            Add your first SSO
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          {configs.map(cfg => (
            <Card key={cfg.id} style={{ border: '1px solid var(--color-border)', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <CardBody style={{ padding: '1.1rem 1.4rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {/* Provider icon */}
                  <div style={{ width: 42, height: 42, borderRadius: '10px', background: '#f8f9fa', border: '1px solid #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <img src={`https://www.google.com/s2/favicons?domain=${cfg.providerDomain}&sz=64`} alt={cfg.provider} style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1d23' }}>{cfg.name}</span>
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                        padding: '2px 8px', borderRadius: '10px',
                        background: cfg.status === 'active' ? '#d1f5e0' : '#f0f0f0',
                        color: cfg.status === 'active' ? '#1a7f47' : '#adb5bd',
                      }}>{cfg.status}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '2px' }}>
                      {cfg.provider} · {cfg.domain} · {cfg.protocol}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                    {/* Status toggle */}
                    <button
                      onClick={() => handleToggleStatus(cfg.id)}
                      title={cfg.status === 'active' ? 'Disable' : 'Enable'}
                      style={{
                        width: 36, height: 20, borderRadius: 10, flexShrink: 0, border: 'none', cursor: 'pointer', padding: 0, position: 'relative',
                        background: cfg.status === 'active' ? 'var(--color-primary, #5b8dee)' : '#dee2e6',
                        transition: 'background 0.2s',
                      }}
                    >
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3,
                        left: cfg.status === 'active' ? 19 : 3,
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                    <button
                      onClick={() => openEdit(cfg)}
                      style={{ background: '#f0f2f5', border: 'none', borderRadius: '7px', padding: '5px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#495057', cursor: 'pointer' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(cfg.id)}
                      style={{ background: '#fff0f0', border: 'none', borderRadius: '7px', padding: '5px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#dc3545', cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Side panel */}
      {showPanel && (
        <>
          <div onClick={() => setShowPanel(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1040 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '380px', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.1)', zIndex: 1050, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1d23' }}>{editingId ? 'Edit SSO Configuration' : 'Add SSO Configuration'}</div>
              <button onClick={() => setShowPanel(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#adb5bd', fontSize: '1.3rem', lineHeight: 1, padding: '4px' }}>×</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
              <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

                {/* Name */}
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d', display: 'block', marginBottom: '5px' }}>Configuration Name</label>
                  <input
                    type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Primary SSO, Dev Team…"
                    style={{ fontSize: '0.875rem', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '7px', width: '100%', outline: 'none' }}
                  />
                </div>

                {/* Identity provider */}
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d', display: 'block', marginBottom: '8px' }}>Identity Provider</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    {SSO_PROVIDERS.map(({ name, domain }) => {
                      const sel = form.provider === name;
                      return (
                        <button key={name} type="button" onClick={() => setForm(f => ({ ...f, provider: name }))} style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
                          border: sel ? '2px solid var(--color-primary, #5b8dee)' : '2px solid #e9ecef',
                          background: sel ? '#f0f5ff' : '#f8f9fa', textAlign: 'left', width: '100%', transition: 'all 0.15s',
                        }}>
                          <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} alt={name} style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: sel ? 'var(--color-primary, #5b8dee)' : '#343a40', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Protocol */}
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d', display: 'block', marginBottom: '8px' }}>Protocol</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {['SAML 2.0', 'OIDC'].map(p => {
                      const sel = form.protocol === p;
                      return (
                        <button key={p} type="button" onClick={() => setForm(f => ({ ...f, protocol: p }))} style={{
                          flex: 1, padding: '0.55rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                          border: sel ? '2px solid var(--color-primary, #5b8dee)' : '2px solid #e9ecef',
                          background: sel ? '#f0f5ff' : '#f8f9fa',
                          color: sel ? 'var(--color-primary, #5b8dee)' : '#6c757d', transition: 'all 0.15s',
                        }}>{p}</button>
                      );
                    })}
                  </div>
                </div>

                {/* Domain */}
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d', display: 'block', marginBottom: '5px' }}>Company domain</label>
                  <input
                    type="text" value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))}
                    placeholder="e.g. yourcompany.com"
                    style={{ fontSize: '0.875rem', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '7px', width: '100%', outline: 'none' }}
                  />
                </div>

                {/* Metadata URL */}
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d', display: 'block', marginBottom: '5px' }}>
                    {form.protocol === 'OIDC' ? 'Discovery URL' : 'Metadata URL'}
                    <span style={{ fontWeight: 400, color: '#adb5bd', marginLeft: '4px' }}>(optional)</span>
                  </label>
                  <input
                    type="text" value={form.metadataUrl} onChange={e => setForm(f => ({ ...f, metadataUrl: e.target.value }))}
                    placeholder="https://..."
                    style={{ fontSize: '0.875rem', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '7px', width: '100%', outline: 'none' }}
                  />
                </div>

                <div style={{ background: '#f0f5ff', borderRadius: '8px', padding: '0.75rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>ℹ️</span>
                  <p style={{ fontSize: '0.72rem', color: '#3d6aad', margin: 0, lineHeight: 1.5 }}>
                    Your IT admin must configure a corresponding application on the identity provider side using your BillsMax callback URL before this can be used.
                  </p>
                </div>

              </form>
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowPanel(false)} style={{ flex: 1, fontSize: '0.875rem', fontWeight: 500, padding: '9px', borderRadius: '7px', border: '1px solid var(--color-border)', background: 'white', color: '#4a5568', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving || !form.name || !form.provider} style={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, padding: '9px', borderRadius: '7px', border: 'none', background: 'var(--color-primary)', color: 'white', cursor: saving ? 'default' : 'pointer', opacity: saving || !form.name || !form.provider ? 0.6 : 1 }}>
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Configuration'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ORG_INTEGRATIONS = [
  { name: 'Slack', domain: 'slack.com', category: 'Notifications', status: 'available' },
  { name: 'Zapier', domain: 'zapier.com', category: 'Automation', status: 'available' },
  { name: 'Custom API / Webhook', domain: 'openid.net', category: 'Custom', status: 'available', isCustom: true },
];

const IntegrationsTab = ({ orgId }) => {
  const settings = useOrgSettings('integrations', orgId);
  const saved = settings.data?.values || [];
  const allEvents = settings.data?.events || [];
  const connected = saved.filter(i => i.enabled).map(i => i.name);
  const [filter, setFilter] = useState('All');

  const categories = ['All', ...Array.from(new Set(ORG_INTEGRATIONS.map(i => i.category)))];
  const filtered = filter === 'All' ? ORG_INTEGRATIONS : ORG_INTEGRATIONS.filter(i => i.category === filter);

  const toggle = (name) => {
    const existing = saved.find(i => i.name === name);
    if (connected.includes(name)) {
      settings.save(saved.map(i => i.name === name ? { ...i, enabled: false } : i));
      return;
    }
    const url = window.prompt(`Paste the ${name} incoming webhook URL (it must start with https://)`, existing?.webhookUrl || '');
    if (!url) return;
    const entry = { name, enabled: true, webhookUrl: url.trim(), events: existing?.events?.length ? existing.events : allEvents };
    settings.save(existing ? saved.map(i => i.name === name ? entry : i) : [...saved, entry])
      .then(err => { if (err) window.alert(err); });
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1d23' }}>Integrations</div>
        <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '2px' }}>
          Connect third-party tools to automate workflows across your team
        </div>
      </div>

      {/* Category filter */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            style={{
              fontSize: '0.72rem', fontWeight: 600, padding: '4px 12px', borderRadius: '20px',
              border: filter === cat ? 'none' : '1px solid #e9ecef',
              background: filter === cat ? 'var(--color-primary)' : '#f8f9fa',
              color: filter === cat ? '#fff' : '#6c757d',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Grid */}
      <Row className="g-3">
        {filtered.map(({ name, domain, category, status, isCustom }) => {
          const isConnected = connected.includes(name);
          const isSoon = status === 'coming_soon';
          return (
            <Col key={name} xs={12} sm={6}>
              <Card style={{
                border: `1px solid ${isConnected ? '#c8e6ff' : 'var(--color-border)'}`,
                borderRadius: '12px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                opacity: isSoon ? 0.65 : 1,
              }}>
                <CardBody style={{ padding: '1rem 1.1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: 38, height: 38, borderRadius: '9px', background: isCustom ? '#f0f5ff' : '#f8f9fa', border: `1px solid ${isCustom ? '#c8e6ff' : '#e9ecef'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {isCustom
                        ? <span style={{ fontSize: '1.1rem' }}>🔗</span>
                        : <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} alt={name} style={{ width: 22, height: 22, objectFit: 'contain' }} />
                      }
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1d23' }}>{name}</span>
                        {isSoon && <span style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '1px 6px', borderRadius: '8px', background: '#f0f0f0', color: '#adb5bd' }}>Soon</span>}
                        {isConnected && <span style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '1px 6px', borderRadius: '8px', background: '#d1f5e0', color: '#1a7f47' }}>Active</span>}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#adb5bd', marginTop: '1px' }}>
                        {isCustom ? 'Webhook or REST API endpoint' : category}
                      </div>
                    </div>
                    {!isSoon && (
                      <button
                        onClick={() => toggle(name)}
                        style={{
                          fontSize: '0.72rem', fontWeight: 600, padding: '5px 12px',
                          borderRadius: '7px', border: isConnected ? '1px solid #dc3545' : 'none',
                          background: isConnected ? '#fff0f0' : 'var(--color-primary)',
                          color: isConnected ? '#dc3545' : '#fff',
                          cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
                        }}
                      >
                        {isConnected ? 'Disconnect' : isCustom ? 'Set up' : 'Connect'}
                      </button>
                    )}
                  </div>
                  {isConnected && (
                    <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                      <div style={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#adb5bd', marginBottom: '4px' }}>Webhook URL</div>
                      <div style={{ fontSize: '0.72rem', color: '#495057', fontFamily: 'monospace', wordBreak: 'break-all' }}>{saved.find(i => i.name === name)?.webhookUrl}</div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

const SectionHeader = ({ title, desc }) => (
  <div style={{ marginBottom: '1rem' }}>
    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1d23' }}>{title}</div>
    {desc && <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '2px' }}>{desc}</div>}
  </div>
);

const SettingRow = ({ icon, label, desc, children, danger }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '1rem', padding: '1rem 1.25rem',
    borderBottom: '1px solid #f5f5f5',
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1, minWidth: 0 }}>
      <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: '1px' }}>{icon}</span>
      <div>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: danger ? '#dc3545' : '#1a1d23' }}>{label}</div>
        {desc && <div style={{ fontSize: '0.72rem', color: '#6c757d', marginTop: '2px', lineHeight: 1.5 }}>{desc}</div>}
      </div>
    </div>
    <div style={{ flexShrink: 0 }}>{children}</div>
  </div>
);

const Toggle = ({ on, onChange }) => (
  <button type="button" onClick={() => onChange(!on)} style={{
    width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', padding: 0, position: 'relative',
    background: on ? 'var(--color-primary, #5b8dee)' : '#dee2e6', transition: 'background 0.2s',
  }}>
    <div style={{
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 3, left: on ? 21 : 3,
      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </button>
);

const SecurityTab = ({ orgId }) => {
  const [kmsProvider, setKmsProvider] = useState('billsmax'); // 'billsmax' | 'aws' | 'azure' | 'gcp'
  const [rotationPeriod, setRotationPeriod] = useState('90'); // days
  const [rotationEnabled, setRotationEnabled] = useState(true);
  const [notifyOnRotation, setNotifyOnRotation] = useState(true);
  const [mfaEnforced, setMfaEnforced] = useState(false);
  const [mfaPolicy, setMfaPolicy] = useState('any'); // 'any' | 'totp' | 'hardware'
  const [sessionTimeout, setSessionTimeout] = useState('8'); // hours
  const [ipWhitelistEnabled, setIpWhitelistEnabled] = useState(false);
  const [ipList, setIpList] = useState('');
  const [signingAlgo, setSigningAlgo] = useState('RS256');
  const [tokenExpiry, setTokenExpiry] = useState('24');
  const [breachDetection, setBreachDetection] = useState(true);
  const [auditRetention, setAuditRetention] = useState('90');
  const [rotating, setRotating] = useState(false);
  const [rotated, setRotated] = useState(false);

  const security = useOrgSettings('security', orgId);
  const [savingSecurity, setSavingSecurity] = useState(false);
  const [savedSecurity, setSavedSecurity] = useState(false);

  useEffect(() => {
    const v = security.data?.values;
    if (!v) return;
    setKmsProvider(v.kmsProvider); setRotationPeriod(String(v.rotationPeriodDays)); setRotationEnabled(v.rotationEnabled);
    setNotifyOnRotation(v.notifyOnRotation); setMfaEnforced(v.mfaEnforced); setMfaPolicy(v.mfaPolicy);
    setSessionTimeout(String(v.sessionTimeoutHours)); setIpWhitelistEnabled(v.ipAllowListEnabled); setIpList(v.ipAllowList || '');
    setSigningAlgo(v.signingAlgorithm); setTokenExpiry(String(v.tokenExpiryHours)); setBreachDetection(v.breachDetection);
    setAuditRetention(String(v.auditRetentionDays));
    // only when the loaded values change
  }, [security.data?.values]);

  const handleRotateNow = async () => {
    setRotating(true);
    try {
      await rotateSigningKey(await security.token(), orgId);
      await security.refresh();
      setRotated(true);
      setTimeout(() => setRotated(false), 3000);
    } catch (e) {
      // the error banner below shows a failed save; a failed rotation leaves the old date in place
    } finally {
      setRotating(false);
    }
  };

  const handleSaveSecurity = async () => {
    setSavingSecurity(true);
    const err = await security.save({
      kmsProvider, rotationPeriodDays: Number(rotationPeriod), rotationEnabled, notifyOnRotation, mfaEnforced, mfaPolicy,
      sessionTimeoutHours: Number(sessionTimeout), ipAllowListEnabled: ipWhitelistEnabled, ipAllowList: ipList,
      signingAlgorithm: signingAlgo, tokenExpiryHours: Number(tokenExpiry), breachDetection, auditRetentionDays: Number(auditRetention),
    });
    setSavingSecurity(false);
    if (!err) { setSavedSecurity(true); setTimeout(() => setSavedSecurity(false), 3000); }
  };

  const formatDate = (value) => value ? new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
  const keyLastRotated = formatDate(security.data?.signingKeyRotatedAt) || 'never (created with the first webhook)';
  const nextRotation = formatDate(security.data?.signingKeyNextRotation) || 'after the first rotation';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* ── Key Management ── */}
      <Card style={{ border: '1px solid var(--color-border)', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        <CardBody style={{ padding: '1.25rem 1.25rem 0' }}>
          <SectionHeader title="Key Management Service (KMS)" desc="Control how encryption keys are stored and managed for this team" />
        </CardBody>

        {/* KMS provider picker */}
        <div style={{ padding: '0 1.25rem 1.25rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.65rem' }}>Key Provider</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            {[
              { key: 'billsmax', label: 'BillsMax Managed', desc: 'Fully managed, zero config', icon: '🔐' },
              { key: 'aws', label: 'AWS KMS', desc: 'Amazon Key Management Service', icon: '☁️', domain: 'aws.amazon.com' },
              { key: 'azure', label: 'Azure Key Vault', desc: 'Microsoft Azure', icon: '🔷', domain: 'azure.microsoft.com' },
              { key: 'gcp', label: 'Google Cloud KMS', desc: 'GCP Cloud KMS', icon: '🔴', domain: 'cloud.google.com' },
            ].map(({ key, label, desc, icon, domain }) => {
              const sel = kmsProvider === key;
              return (
                <button key={key} type="button" onClick={() => setKmsProvider(key)} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.75rem',
                  borderRadius: '10px', cursor: 'pointer', textAlign: 'left', width: '100%',
                  border: sel ? '2px solid var(--color-primary, #5b8dee)' : '2px solid #e9ecef',
                  background: sel ? '#f0f5ff' : '#f8f9fa', transition: 'all 0.15s',
                }}>
                  <div style={{ width: 32, height: 32, borderRadius: '8px', background: '#fff', border: '1px solid #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {domain
                      ? <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} alt={label} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                      : <span style={{ fontSize: '1rem' }}>{icon}</span>
                    }
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: sel ? 'var(--color-primary, #5b8dee)' : '#343a40' }}>{label}</div>
                    <div style={{ fontSize: '0.65rem', color: '#adb5bd', marginTop: '1px' }}>{desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {kmsProvider !== 'billsmax' && (
            <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c757d', marginBottom: '6px' }}>ARN / Key URI</div>
              <input
                type="text"
                placeholder={kmsProvider === 'aws' ? 'arn:aws:kms:eu-west-1:…' : kmsProvider === 'azure' ? 'https://vault.azure.net/keys/…' : 'projects/…/locations/…/keyRings/…'}
                style={{ fontSize: '0.8rem', padding: '7px 10px', border: '1px solid var(--color-border)', borderRadius: '6px', width: '100%', outline: 'none', fontFamily: 'monospace' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.6rem', padding: '0.6rem 0.75rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '7px' }}>
                <span style={{ fontSize: '0.8rem' }}>⚠️</span>
                <span style={{ fontSize: '0.7rem', color: '#856404', lineHeight: 1.4 }}>BillsMax must be granted <strong>Encrypt</strong> and <strong>Decrypt</strong> permissions on this key before saving.</span>
              </div>
            </div>
          )}
        </div>

        {/* Key status */}
        <div style={{ borderTop: '1px solid #f5f5f5' }}>
          <SettingRow icon="🗝️" label="Current Signing Key" desc={`Last rotated: ${keyLastRotated}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {rotated && <span style={{ fontSize: '0.72rem', color: '#1a7f47', fontWeight: 600 }}>✓ Rotated</span>}
              <button
                onClick={handleRotateNow}
                disabled={rotating}
                style={{ fontSize: '0.75rem', fontWeight: 600, padding: '5px 12px', borderRadius: '7px', border: '1px solid #e9ecef', background: '#fff', color: '#495057', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {rotating ? 'Rotating…' : 'Rotate now'}
              </button>
            </div>
          </SettingRow>
        </div>
      </Card>

      {/* ── Key Rotation Policy ── */}
      <Card style={{ border: '1px solid var(--color-border)', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        <CardBody style={{ padding: '1.25rem 1.25rem 0' }}>
          <SectionHeader title="Key Rotation Policy" desc="Define how often encryption and signing keys are automatically rotated" />
        </CardBody>
        <SettingRow icon="🔄" label="Automatic Key Rotation" desc="Keys will be rotated on schedule without manual intervention">
          <Toggle on={rotationEnabled} onChange={setRotationEnabled} />
        </SettingRow>
        {rotationEnabled && (
          <>
            <SettingRow icon="📅" label="Rotation Period" desc={`Next rotation: ${nextRotation}`}>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {['30', '60', '90', '180', '365'].map(d => (
                  <button key={d} type="button" onClick={() => setRotationPeriod(d)} style={{
                    padding: '4px 10px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                    border: rotationPeriod === d ? 'none' : '1px solid #e9ecef',
                    background: rotationPeriod === d ? 'var(--color-primary, #5b8dee)' : '#f8f9fa',
                    color: rotationPeriod === d ? '#fff' : '#6c757d',
                  }}>{d}d</button>
                ))}
              </div>
            </SettingRow>
            <SettingRow icon="🔔" label="Notify on Rotation" desc="Alert team owners when a key is rotated">
              <Toggle on={notifyOnRotation} onChange={setNotifyOnRotation} />
            </SettingRow>
          </>
        )}
        <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #f5f5f5' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.5rem' }}>Signing Algorithm</div>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {['RS256', 'RS384', 'PS256', 'ES256'].map(algo => (
              <button key={algo} type="button" onClick={() => setSigningAlgo(algo)} style={{
                padding: '4px 10px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                border: signingAlgo === algo ? 'none' : '1px solid #e9ecef',
                background: signingAlgo === algo ? 'var(--color-primary, #5b8dee)' : '#f8f9fa',
                color: signingAlgo === algo ? '#fff' : '#6c757d',
              }}>{algo}</button>
            ))}
          </div>
        </div>
      </Card>

      {/* ── Access & Session Policy ── */}
      <Card style={{ border: '1px solid var(--color-border)', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        <CardBody style={{ padding: '1.25rem 1.25rem 0' }}>
          <SectionHeader title="Access & Session Policy" desc="Control how members access the team and how long sessions last" />
        </CardBody>
        <SettingRow icon="🛡️" label="Enforce MFA for all members" desc="Members without MFA configured will be blocked from accessing this org">
          <Toggle on={mfaEnforced} onChange={setMfaEnforced} />
        </SettingRow>
        {mfaEnforced && (
          <div style={{ padding: '0 1.25rem 1rem', borderBottom: '1px solid #f5f5f5' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.5rem', marginTop: '0.25rem' }}>Accepted MFA Methods</div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {[
                { key: 'any', label: 'Any method' },
                { key: 'totp', label: 'Authenticator app only' },
                { key: 'hardware', label: 'Hardware key only' },
              ].map(({ key, label }) => (
                <button key={key} type="button" onClick={() => setMfaPolicy(key)} style={{
                  padding: '4px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                  border: mfaPolicy === key ? 'none' : '1px solid #e9ecef',
                  background: mfaPolicy === key ? 'var(--color-primary, #5b8dee)' : '#f8f9fa',
                  color: mfaPolicy === key ? '#fff' : '#6c757d',
                }}>{label}</button>
              ))}
            </div>
          </div>
        )}
        <SettingRow icon="⏱️" label="Session Timeout" desc="Automatically sign members out after inactivity">
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {['1', '4', '8', '24'].map(h => (
              <button key={h} type="button" onClick={() => setSessionTimeout(h)} style={{
                padding: '4px 10px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                border: sessionTimeout === h ? 'none' : '1px solid #e9ecef',
                background: sessionTimeout === h ? 'var(--color-primary, #5b8dee)' : '#f8f9fa',
                color: sessionTimeout === h ? '#fff' : '#6c757d',
              }}>{h}h</button>
            ))}
          </div>
        </SettingRow>
        <SettingRow icon="🏷️" label="Token Expiry" desc="How long access tokens remain valid">
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {['1', '8', '24', '72'].map(h => (
              <button key={h} type="button" onClick={() => setTokenExpiry(h)} style={{
                padding: '4px 10px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                border: tokenExpiry === h ? 'none' : '1px solid #e9ecef',
                background: tokenExpiry === h ? 'var(--color-primary, #5b8dee)' : '#f8f9fa',
                color: tokenExpiry === h ? '#fff' : '#6c757d',
              }}>{h}h</button>
            ))}
          </div>
        </SettingRow>
        <SettingRow icon="🌍" label="IP Allow-list" desc="Restrict access to specific IP ranges or CIDR blocks">
          <Toggle on={ipWhitelistEnabled} onChange={setIpWhitelistEnabled} />
        </SettingRow>
        {ipWhitelistEnabled && (
          <div style={{ padding: '0 1.25rem 1rem', borderBottom: '1px solid #f5f5f5' }}>
            <textarea
              value={ipList}
              onChange={e => setIpList(e.target.value)}
              placeholder={'192.168.1.0/24\n10.0.0.0/8\n203.0.113.42'}
              rows={3}
              style={{ fontSize: '0.78rem', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: '7px', width: '100%', outline: 'none', fontFamily: 'monospace', resize: 'vertical' }}
            />
            <div style={{ fontSize: '0.68rem', color: '#adb5bd', marginTop: '4px' }}>One IP or CIDR per line</div>
          </div>
        )}
      </Card>

      {/* ── Threat Detection ── */}
      <Card style={{ border: '1px solid var(--color-border)', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        <CardBody style={{ padding: '1.25rem 1.25rem 0' }}>
          <SectionHeader title="Threat Detection" desc="Automated protections against suspicious activity" />
        </CardBody>
        <SettingRow icon="🚨" label="Breached password detection" desc="Block sign-ins when credentials appear in known breach databases">
          <Toggle on={breachDetection} onChange={setBreachDetection} />
        </SettingRow>
        <SettingRow icon="🤖" label="Bot detection" desc="Challenge or block automated login attempts">
          <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 8px', borderRadius: '10px', background: '#f0f0f0', color: '#adb5bd' }}>Coming Soon</span>
        </SettingRow>
        <SettingRow icon="📍" label="Suspicious location detection" desc="Alert when a member signs in from an unusual location">
          <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 8px', borderRadius: '10px', background: '#f0f0f0', color: '#adb5bd' }}>Coming Soon</span>
        </SettingRow>
      </Card>

      {/* ── Audit Log Retention ── */}
      <Card style={{ border: '1px solid var(--color-border)', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        <CardBody style={{ padding: '1.25rem 1.25rem 0' }}>
          <SectionHeader title="Audit Log Retention" desc="How long security events and access logs are retained" />
        </CardBody>
        <div style={{ padding: '0 1.25rem 1.25rem' }}>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {[
              { key: '30', label: '30 days' },
              { key: '90', label: '90 days' },
              { key: '180', label: '6 months' },
              { key: '365', label: '1 year' },
              { key: '730', label: '2 years' },
            ].map(({ key, label }) => (
              <button key={key} type="button" onClick={() => setAuditRetention(key)} style={{
                padding: '5px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                border: auditRetention === key ? 'none' : '1px solid #e9ecef',
                background: auditRetention === key ? 'var(--color-primary, #5b8dee)' : '#f8f9fa',
                color: auditRetention === key ? '#fff' : '#6c757d',
              }}>{label}</button>
            ))}
          </div>
          <div style={{ fontSize: '0.7rem', color: '#adb5bd', marginTop: '0.6rem' }}>
            Logs older than the retention period are permanently deleted
          </div>
        </div>
      </Card>

      {/* Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: '1rem' }}>
        {security.error && <div style={{ fontSize: '0.8rem', color: 'var(--color-danger)', alignSelf: 'center', marginRight: '1rem' }}>{security.error}</div>}
        <button onClick={handleSaveSecurity} disabled={savingSecurity} style={{ fontSize: '0.875rem', fontWeight: 600, padding: '9px 24px', borderRadius: '8px', border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: savingSecurity ? 'default' : 'pointer', opacity: savingSecurity ? 0.7 : 1 }}>
          {savingSecurity ? 'Saving…' : savedSecurity ? 'Saved' : 'Save Security Settings'}
        </button>
      </div>

    </div>
  );
};

const GeneralTab = ({ org }) => (
  <Row className="g-3">
    <Col xs={12} md={6}>
      <Card style={{ border: '1px solid var(--color-border)', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <CardBody style={{ padding: '1.75rem' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1d23', marginBottom: '1.25rem' }}>General</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
            <Field label="Team name" value={org.orgName} />
            <Field label="Team ID" value={org.orgId} />
            <Field label="Identifier" value={org.orgIdentifier || null} />
            <Field label="Status" value={org.isClaimed === 1 ? 'Claimed' : 'Unclaimed'} />
          </div>
        </CardBody>
      </Card>
    </Col>
    <Col xs={12} md={6}>
      <Card style={{ border: '1px solid var(--color-border)', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <CardBody style={{ padding: '1.75rem' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1d23', marginBottom: '1.25rem' }}>Configuration</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
            <Field label="Business Type" value={BUSINESS_TYPE_MAP[org.businessType] || String(org.businessType)} />
            <Field label="Currency" value={CURRENCY_MAP[org.monetaryCurrency] || `Code ${org.monetaryCurrency}`} />
          </div>
        </CardBody>
      </Card>
    </Col>
  </Row>
);

const AddMemberPanel = ({ orgId, onClose, onAdded }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ emailAddress: '', firstName: '', lastName: '', role: 'member' });
  const [error, setError] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    setError(null);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      const { role, ...member } = form;
      await addOrganisationMember({ orgId: Number(orgId), ...member }, token);
      // New members join as Member; promote them straight away when Admin was picked.
      if (role === 'admin') {
        const [roles, members] = await Promise.all([fetchOrgRoles(orgId, token), fetchOrganisationMembers(orgId, token)]);
        const admin = (roles?.values || []).find(r => r.name === 'Admin');
        const added = (members?.userList || []).find(m => (m.emailAddress || '').toLowerCase() === member.emailAddress.toLowerCase());
        if (admin && added) await setOrgMemberRole({ orgId: Number(orgId), userId: added.userId, roleId: admin.roleId }, token);
      }
      onAdded();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to add member');
      setAdding(false);
    }
  };

  const ROLES = [
    { value: 'admin',  label: 'Admin',  desc: 'Manage members, suppliers and settings' },
    { value: 'member', label: 'Member', desc: 'Can see the team and its members; cannot change them' },
  ];

  const fields = [
    { key: 'emailAddress', label: 'Email Address', type: 'email', required: true, placeholder: 'name@example.com' },
    { key: 'firstName', label: 'First Name', type: 'text', placeholder: 'First name' },
    { key: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Last name' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)',
          zIndex: 1040, transition: 'opacity 0.2s',
        }}
      />
      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '360px',
        background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        zIndex: 1050, display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1d23' }}>Add Member</div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', padding: '4px', display: 'flex', alignItems: 'center' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleAdd} style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', overflowY: 'auto' }}>
          {fields.map(({ key, label, type, required, placeholder }) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>
                {label}{required && <span style={{ color: 'var(--color-danger)', marginLeft: '2px' }}>*</span>}
              </label>
              <input
                type={type}
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                required={required}
                style={{
                  fontSize: '0.875rem', padding: '8px 12px',
                  border: '1px solid var(--color-border)', borderRadius: '7px',
                  outline: 'none', color: '#1a1d23', width: '100%',
                }}
              />
            </div>
          ))}

          {/* Role */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>
              Role <span style={{ color: 'var(--color-danger)', marginLeft: '2px' }}>*</span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {ROLES.map(({ value, label, desc }) => {
                const sel = form.role === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, role: value }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.6rem 0.85rem', borderRadius: '8px', cursor: 'pointer',
                      border: sel ? '2px solid var(--color-primary, #5b8dee)' : '2px solid #e9ecef',
                      background: sel ? '#f0f5ff' : '#f8f9fa',
                      textAlign: 'left', width: '100%', transition: 'all 0.15s',
                    }}
                  >
                    {/* Role colour dot */}
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: { owner: '#f59e0b', admin: 'var(--color-primary, #5b8dee)', member: '#10b981', viewer: '#adb5bd' }[value],
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: sel ? 'var(--color-primary, #5b8dee)' : '#1a1d23' }}>{label}</div>
                      <div style={{ fontSize: '0.68rem', color: '#adb5bd', marginTop: '1px' }}>{desc}</div>
                    </div>
                    {sel && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary, #5b8dee)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <div style={{ fontSize: '0.8rem', color: 'var(--color-danger)', padding: '8px 12px', background: '#fff5f5', borderRadius: '6px', border: '1px solid #fecaca' }}>
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1, fontSize: '0.875rem', fontWeight: 500, padding: '9px',
              borderRadius: '7px', border: '1px solid var(--color-border)',
              background: 'white', color: '#4a5568', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-member-form"
            disabled={adding}
            onClick={handleAdd}
            style={{
              flex: 1, fontSize: '0.875rem', fontWeight: 600, padding: '9px',
              borderRadius: '7px', border: 'none',
              background: 'var(--color-primary)', color: 'white',
              cursor: adding ? 'default' : 'pointer', opacity: adding ? 0.7 : 1,
            }}
          >
            {adding ? 'Adding...' : 'Add Member'}
          </button>
        </div>
      </div>
    </>
  );
};

const EditMemberPanel = ({ member, orgId, onClose, onSaved }) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState(member.roleId || null);
  const [roleSaving, setRoleSaving] = useState(false);

  useEffect(() => {
    if (member.isOwner) return;
    (async () => {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      const data = await fetchOrgRoles(orgId, token).catch(() => null);
      setRoles(data?.values || []);
    })();
  }, [orgId, member.isOwner, getAccessTokenSilently]);

  const handleRoleChange = async (newRoleId) => {
    if (newRoleId === roleId) return;
    setRoleSaving(true);
    setError(null);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      await setOrgMemberRole({ orgId: Number(orgId), userId: member.userId, roleId: newRoleId }, token);
      setRoleId(newRoleId);
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to change the role');
    } finally {
      setRoleSaving(false);
    }
  };
  // Core only lets a user replace their own key, so rotating is offered on your own row only.
  const isSelf = !!user?.email && (member.emailAddress || '').toLowerCase() === user.email.toLowerCase();
  const contactId = member.contactId || null;
  const [form, setForm] = useState({
    emailAddress: member.emailAddress || '',
    firstName: member.firstName || '',
    lastName: member.lastName || '',
  });
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState(null);
  const [rotateMsg, setRotateMsg] = useState(null);
  // The key is only ever shown straight after rotating it; the API never returns a stored key.
  const [key, setKey] = useState(null);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      await updateOrganisationMember({ orgId: Number(orgId), userId: member.userId, contactId, ...form }, token);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update member');
      setSaving(false);
    }
  };

  const handleRotateKey = async () => {
    setRotating(true);
    setRotateMsg(null);
    setError(null);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      const data = await rotateOrgMemberKey({ orgId: Number(orgId), userId: member.userId }, token);
      setKey(data.key || null);
      setRotateMsg('Security key rotated. Copy it now: it will not be shown again.');
    } catch (err) {
      setError(err.message || 'Failed to rotate key');
    } finally {
      setRotating(false);
    }
  };

  const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );

  const fields = [
    { key: 'emailAddress', label: 'Email Address', type: 'email', placeholder: 'name@example.com' },
    { key: 'firstName', label: 'First Name', type: 'text', placeholder: 'First name' },
    { key: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Last name' },
  ];

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1040 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '380px', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 1050, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1d23' }}>Edit Member</div>
            <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '1px' }}>#{member.userId}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', padding: '4px', display: 'flex', alignItems: 'center' }}>
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSave} style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', overflowY: 'auto' }}>
          {fields.map(({ key, label, type, placeholder }) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>
                {label}
              </label>
              <input
                type={type}
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                style={{ fontSize: '0.875rem', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '7px', outline: 'none', color: '#1a1d23', width: '100%' }}
              />
            </div>
          ))}

          {!member.isOwner && roles.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Role</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {roles.map(r => (
                  <button key={r.roleId} type="button" disabled={roleSaving} onClick={() => handleRoleChange(r.roleId)} style={{
                    flex: 1, fontSize: '0.85rem', fontWeight: 600, padding: '8px 12px', borderRadius: '7px', cursor: roleSaving ? 'default' : 'pointer',
                    border: roleId === r.roleId ? 'none' : '1px solid var(--color-border)',
                    background: roleId === r.roleId ? 'var(--color-primary, #5b8dee)' : 'white',
                    color: roleId === r.roleId ? 'white' : '#1a1d23',
                  }}>{r.name}</button>
                ))}
              </div>
            </div>
          )}

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Security</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Encryption Key</label>
              <div style={{ fontSize: '0.875rem', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '7px', color: '#1a1d23', background: '#fafbfc', fontFamily: 'monospace', letterSpacing: key ? '0.02em' : '0.15em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'all' }}>
                {key || '••••••••••••••••'}
              </div>
            </div>
            {isSelf && <button
              type="button"
              onClick={handleRotateKey}
              disabled={rotating}
              style={{ fontSize: '0.875rem', fontWeight: 500, padding: '8px 14px', borderRadius: '7px', border: '1px solid var(--color-border)', background: 'white', color: '#1a1d23', cursor: rotating ? 'default' : 'pointer', opacity: rotating ? 0.7 : 1, textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
              {rotating ? 'Rotating...' : 'Rotate Security Key'}
            </button>}
            {rotateMsg && <div style={{ fontSize: '0.8rem', color: '#16a34a', padding: '7px 10px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>{rotateMsg}</div>}
          </div>

          {error && (
            <div style={{ fontSize: '0.8rem', color: 'var(--color-danger)', padding: '8px 12px', background: '#fff5f5', borderRadius: '6px', border: '1px solid #fecaca' }}>{error}</div>
          )}
        </form>

        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '0.75rem' }}>
          <button type="button" onClick={onClose} style={{ flex: 1, fontSize: '0.875rem', fontWeight: 500, padding: '9px', borderRadius: '7px', border: '1px solid var(--color-border)', background: 'white', color: '#4a5568', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, padding: '9px', borderRadius: '7px', border: 'none', background: 'var(--color-primary)', color: 'white', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

const MembersTab = ({ orgId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      const data = await fetchOrganisationMembers(orgId, token).catch(() => null);
      const list = data?.userList || (Array.isArray(data) ? data : []);
      setMembers(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orgId]);

  return (
    <>
      {addPanelOpen && (
        <AddMemberPanel orgId={orgId} onClose={() => setAddPanelOpen(false)} onAdded={load} />
      )}
      {selectedMember && (
        <EditMemberPanel member={selectedMember} orgId={orgId} onClose={() => setSelectedMember(null)} onSaved={load} />
      )}
    <Card style={{ border: '1px solid var(--color-border)', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1d23' }}>
          {loading ? 'Loading...' : `${members.length} member${members.length !== 1 ? 's' : ''}`}
        </div>
        <button
          onClick={() => setAddPanelOpen(true)}
          style={{ fontSize: '0.78rem', fontWeight: 600, padding: '4px 12px', borderRadius: '6px', border: '1px solid var(--color-primary)', background: 'white', color: 'var(--color-primary)', cursor: 'pointer' }}
        >
          Add Member
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#adb5bd', fontSize: '0.875rem' }}>
          <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
          <div>Loading members...</div>
        </div>
      ) : (
        <table className="table mb-0" style={{ fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#fafbfc', borderBottom: '1px solid #e9ecef' }}>
              {[['ID', '80px'], ['Name', null], ['Email', null], ['Role', '140px'], ['', '60px']].map(([label, width]) => (
                <th key={label} style={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d', padding: '0.75rem 1.25rem', border: 'none', whiteSpace: 'nowrap', ...(width ? { width, minWidth: width } : {}) }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.length > 0 ? members.map((m, i) => {
              const id = m.userId;
              const name = [m.firstName, m.lastName].filter(Boolean).join(' ') || '—';
              const email = m.emailAddress || '—';
              const role = m.isOwner ? 'Owner' : (m.roleName || m.role || '—');
              return (
                <tr key={i} style={{ borderBottom: '1px solid #f7f8fa', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fafbfc'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                >
                  <td style={{ padding: '0.85rem 1.25rem', color: '#adb5bd', fontWeight: 500, verticalAlign: 'middle' }}>
                    {id ? `#${id}` : '—'}
                  </td>
                  <td style={{ padding: '0.85rem 1.25rem', fontWeight: 600, color: '#1a1d23', verticalAlign: 'middle' }}>
                    {name}
                  </td>
                  <td style={{ padding: '0.85rem 1.25rem', color: '#4a5568', verticalAlign: 'middle' }}>
                    {email}
                  </td>
                  <td style={{ padding: '0.85rem 1.25rem', verticalAlign: 'middle' }}>
                    {role !== '—' ? (
                      <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: '12px', background: '#f0f4ff', color: '#4361ee', fontSize: '0.75rem', fontWeight: 600 }}>
                        {role}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '0.85rem 1.25rem', verticalAlign: 'middle' }}>
                    <button onClick={() => setSelectedMember(m)} className="btn-pill btn-pill-blue" style={{ fontSize: '0.75rem', padding: '3px 10px' }}>
                      Edit
                    </button>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="5" style={{ padding: '4rem', textAlign: 'center', color: '#adb5bd', fontSize: '0.875rem' }}>
                  No members found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </Card>
    </>
  );
};

const OrganisationComponent = () => {
  const { orgId } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const history = useHistory();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        let token = null;
        try { token = await getAccessTokenSilently(); } catch (e) {}
        const data = await fetchOrganisation(orgId, token);
        if (data?.func_status === 1) setOrg(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId, getAccessTokenSilently]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      await deleteOrganisation(orgId, token);
      history.push('/settings?tab=teams');
    } catch (e) {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div style={{ paddingBottom: '3rem' }}>
      <div className="mb-4" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2 className="page-title mb-1">{loading ? 'Team' : (org?.orgName || 'Team')}</h2>
          <p className="page-subtitle">Team settings</p>
        </div>
        {!loading && org && (
          confirmDelete ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>Delete team?</span>
              <button onClick={handleDelete} disabled={deleting} className="btn-pill btn-pill-red" style={{ fontSize: '0.75rem', padding: '3px 10px' }}>
                {deleting ? 'Deleting...' : 'Confirm'}
              </button>
              <button onClick={() => setConfirmDelete(false)} className="btn-pill btn-pill-grey" style={{ fontSize: '0.75rem', padding: '3px 10px' }}>
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setConfirmDelete(true)} className="btn-pill btn-pill-red" style={{ fontSize: '0.75rem', padding: '3px 10px' }}>
              Delete
            </button>
          )
        )}
      </div>

      {loading ? (
        <div style={{ color: '#adb5bd', fontSize: '0.875rem' }}>Loading...</div>
      ) : !org ? (
        <div style={{ color: '#adb5bd', fontSize: '0.875rem' }}>Team not found.</div>
      ) : (
        <>
          <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '1.5rem' }}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
                  padding: '0.6rem 1.1rem',
                  fontSize: '0.88rem',
                  fontWeight: activeTab === tab.id ? 600 : 500,
                  color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-muted)',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'color 0.15s ease, border-color 0.15s ease',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'general' && <GeneralTab org={org} />}
          {activeTab === 'members' && <MembersTab orgId={orgId} />}
          {activeTab === 'sso' && <SsoTab orgId={orgId} />}
          {activeTab === 'integrations' && <IntegrationsTab orgId={orgId} />}
          {activeTab === 'security' && <SecurityTab orgId={orgId} />}
        </>
      )}
    </div>
  );
};

export default withAuthenticationRequired(OrganisationComponent, {
  onRedirecting: () => <Loading />,
});
